import { ParseError, isValidPhoneNumber, parsePhoneNumberWithError, validatePhoneNumberLength } from "libphonenumber-js";
import moment from "moment";
import { countCharacters } from "@/helpers/utilities";
import { countCodePoints } from "@common-ui/utilities/characterCount";
import { FieldType } from "@/types/forms/FieldType";
import { FieldValidationType } from "@/types/forms/FieldValidationType";
import {
	formatMaxLengthErrorMessage,
	EMAIL_MAX_LENGTH,
	isEmailWithinMaxLength,
	isPostmarkCompatibleEmail,
	parsePositiveMaxLength,
	PUBLIC_FORM_DEFAULT_MAX_LENGTH,
	PUBLIC_FORM_NUMBER_MAX_LENGTH
} from "@liveswitch-contact/types";

/**
 * Formats accepted for a web-form Date field. The native `<input type="date">`
 * always emits ISO `YYYY-MM-DD`, but a browser that falls back to a plain text
 * input (or a user pasting) can yield the localized `MM/DD/YYYY` / `DD/MM/YYYY`
 * forms, so we accept all three and parse in strict mode (LSP-16446).
 */
const ACCEPTED_DATE_FORMATS = ["YYYY-MM-DD", "MM/DD/YYYY", "DD/MM/YYYY"] as const;

/** Default copy for the Date-field validator — specific about the expected format (LSP-16446). */
export const DEFAULT_DATE_ERROR = "Enter a valid date (MM/DD/YYYY).";

/**
 * Field length constants
 */
export const FIELD_LENGTHS = {
	/**
	 * Default MaxLength the API injects for non-email form fields saved without an explicit MaxLength
	 * validator (contact-api src/controllers/forms.ts pushes MaxLength:"250"; email fields use the
	 * shared 254-character ceiling). The form-builder preview mirrors this default, but
	 * only for the free-text types that actually surface a counter (see inheritsDefaultMaxLength in
	 * WebFormV2.vue), so the limit is visible up-front before the save round-trip materializes the
	 * validator (LSP-16444). Guarded by
	 * apps/contact-api/tests/controllers/formFieldDefaultMaxLengthGuard.unit.test.ts.
	 */
	FORM_FIELD_DEFAULT_MAX: PUBLIC_FORM_DEFAULT_MAX_LENGTH,
	/**
	 * Maximum character length for a Number form field (LSP-16444, per Oliver's review). Unlike the
	 * generic 250 default, a Number field has its own fixed character cap shown up-front. This is a
	 * browser safety limit, not the API's per-field MaxLength contract, so the preview applies it regardless
	 * of the API's injected MaxLength — see getMaxLength/setupValidationRules in WebFormV2.vue. The API
	 * continues to validate the saved field's configured MaxLength for non-browser integrations. It is a
	 * tighter constraint than FIELD_NUMBER_BOUNDS for inputs that fit MAX_SAFE_INTEGER but exceed 15
	 * characters (e.g. a 16-digit value below MAX_SAFE_INTEGER).
	 */
	NUMBER: PUBLIC_FORM_NUMBER_MAX_LENGTH,
	/** Maximum length for first name and last name fields */
	NAME: 45,
	/** Maximum length for email address fields */
	EMAIL: EMAIL_MAX_LENGTH,
	/** Maximum length for text fields */
	TEXT: 125,
	/**
	 * Maximum accepted length for a phone-number input (LSP-16692). Deliberately set to
	 * libphonenumber-js's own MAX_INPUT_STRING_LENGTH (250): at or under it we let the
	 * library decide (it is fast and self-bounds), so we never reject a value the parser
	 * would have accepted — even one padded with many internal separators/spaces. Past it
	 * the library treats the string as TOO_LONG anyway, so short-circuiting first changes
	 * no verdict while skipping the repeated parse/format that vue-tel-input runs on every
	 * reactive keystroke (and avoiding a multi-megabyte cache key) — that runaway work on a
	 * huge paste is what pins the browser tab until it crashes.
	 */
	PHONE_INPUT: 250,
	/**
	 * Maximum length for the guided-instruction intro (LSP-16450). Counted as graphemes, not
	 * UTF-16 code units — callers pair this with countCharactersTrimmed (Intl.Segmenter) for the
	 * counter and rule, so an emoji costs 1 of 250 rather than 2 (which native maxlength would do).
	 */
	INTRO: 250,
	/** Maximum length for a project Sheet's title — matches DB VARCHAR(255) */
	SHEET_TITLE: 255,
	/** Maximum length for a project Sheet's subtitle — matches DB VARCHAR(255) */
	SHEET_SUBTITLE: 255,
	/**
	 * Maximum length for a project Sheet's notes body (LSP-15418).
	 * Must stay in sync with SHEET_NOTES_MAX_LENGTH in
	 * apps/contact-api/src/controllers/sheets.ts and
	 * apps/contact-mobile/shared/src/commonMain/kotlin/com/liveswitch/contact/ui/screens/projects/sheets/SheetEditorScreen.kt.
	 */
	SHEET_NOTES: 5000
} as const;

/**
 * Safe numeric bounds applied to Number form fields when the admin hasn't
 * configured an explicit MaxValue/MinValue validator. Number.MAX_SAFE_INTEGER
 * is the threshold beyond which JavaScript arithmetic loses precision, so any
 * input outside this range would silently corrupt before reaching the API.
 */
export const FIELD_NUMBER_BOUNDS = {
	MAX: Number.MAX_SAFE_INTEGER,
	MIN: -Number.MAX_SAFE_INTEGER
} as const;

/**
 * Matches the shared public-form MaxLength parser used by contact-api: malformed and
 * non-positive values use the API default instead of becoming an impossible UI cap.
 */
export { formatMaxLengthErrorMessage, parsePositiveMaxLength };

/**
 * Free-text field types that inherit the API's default MaxLength. The forms controller injects the
 * default on save, while the builder preview sees the pre-save shape; this keeps the live counter
 * and over-limit rule aligned before that round trip. Other field types do not expose this counter.
 */
export function inheritsDefaultMaxLength(field: { type?: FieldType }): boolean {
	return field.type === FieldType.Text || field.type === FieldType.TextArea || field.type === FieldType.Email;
}

/** Minimal shape both public-form variants pass to the field-resolution predicates below. */
interface FieldLike {
	type?: FieldType;
	FieldValidators?: Array<{ type?: FieldValidationType } | null | undefined> | null;
}

/**
 * Whether a form field is an EMAIL input, by declared type or by carrying an Email validator.
 *
 * Shared by WebForm.vue and WebFormV2.vue (LSP-18267) so the two can provably not drift. Both had
 * their own copy and they disagreed: V2 accepted either arm, V1 accepted only the validator arm.
 * That mattered once the Name-cap ceiling started asking the same question — a field with
 * `type: Email` and no Email validator (nothing in contact-api's cleanFormFields pairs them, and no
 * DB constraint enforces it) answered "yes" for the cap and "no" for V1's rendering branch, so V1
 * matched no branch at all and rendered no input. One predicate, one answer.
 */
export function resolvesToEmailField(field: FieldLike): boolean {
	return field.type === FieldType.Email || !!field.FieldValidators?.some((validator) => validator?.type === FieldValidationType.Email);
}

/** Phone counterpart of resolvesToEmailField — same reasoning, same drift risk (LSP-18267). */
export function resolvesToPhoneField(field: FieldLike): boolean {
	return field.type === FieldType.Phone || !!field.FieldValidators?.some((validator) => validator?.type === FieldValidationType.Phone);
}

/**
 * Whether a field is subject to the FIELD_LENGTHS.NAME ceiling (LSP-18267).
 *
 * Keyed on validator presence, never on `field.type` alone: WebForm.vue reassigns `field.type` to
 * Email/Phone in place while building its rules, so reading the type made the ceiling depend on
 * validator storage order and made the two variants disagree. An email or phone input keeps its own
 * longer limit rather than being squeezed into a name-length cap.
 */
export function isNameCappedField(field: FieldLike): boolean {
	return !resolvesToEmailField(field) && !resolvesToPhoneField(field) && field.type === FieldType.Name;
}

export function resolvePublicFormMaxLength(field: FieldLike, validator: { value?: unknown } | null | undefined): number | undefined {
	if (resolvesToEmailField(field)) return EMAIL_MAX_LENGTH;
	return parsePositiveMaxLength(validator?.value);
}

export interface ValidationRule {
	isValid: (v: any) => boolean | string;
}

export interface ValidationConfig {
	[key: string]: {
		rules: Array<ValidationRule>;
	};
}

const isString = (v: string | boolean) => {
	return typeof v === "string";
};

/**
 * Safely coerces `v` to text for a code-point length rule's `str = v?.toString().trim()` step, never
 * letting a hostile `toString` throw into Vuetify's own `validate()` loop
 * (`node_modules/vuetify/lib/composables/validation.js`) the way `MaxCodePointsValidation`/
 * `MaxCodePointsValidationUntrimmed` below used to (LSP-16852/16853/16854/16855/16859 break-it round 1).
 * `{ toString: () => Symbol("x") }` is the hostile shape: the call itself does not throw, but the
 * `Symbol` it returns has no `.trim()` and is not a `string`, so the very next step used to throw
 * instead — aborting the WHOLE `validate()` run so no rule after the broken one, including this one,
 * ever ran to report the real overflow.
 *
 * `null`/`undefined` still coerce to `""` (an empty, always-valid string, matching every other rule in
 * this file), exactly as before this guard. Any OTHER value that cannot be read as a real string
 * returns `undefined` — a distinct signal from `""` — so the caller can fail CLOSED (report the
 * overflow message) instead of treating an unreadable value as blank.
 */
function stringifyForLengthRule(v: unknown): string | undefined {
	if (v === null || v === undefined) {
		return "";
	}
	try {
		if (Array.isArray(v)) {
			return v.join(", ");
		}
		const stringified = (v as { toString?: () => unknown }).toString?.();
		return typeof stringified === "string" ? stringified : undefined;
	} catch {
		return undefined;
	}
}

class VuetifyValidation<T extends ValidationConfig> {
	private config: T;
	constructor(config: T) {
		this.config = config;
	}
	validate<T extends ValidationConfig>(field: Extract<keyof T, string>, value: any): boolean | string {
		const rules = this.config[field].rules;
		for (let i = 0, l = rules.length; i < l; i++) {
			const result = rules[i].isValid(value);
			if (isString(result)) return result;
		}
		return true;
	}

	static EmailValidation = (errorTpl: string, maxLengthErrorTpl = errorTpl): ValidationRule => {
		return {
			isValid: (value: unknown) => {
				if (value === null || value === undefined || value === "") return true;
				if (typeof value !== "string") return errorTpl;
				if (!isEmailWithinMaxLength(value)) return maxLengthErrorTpl;
				const unsupportedCharacter = value.match(/[^\x00-\x7F]/u)?.[0];
				// Keep invisible/control characters on the generic syntax path; never echo them into the UI.
				if (unsupportedCharacter && !/[\p{C}\p{Z}]/u.test(unsupportedCharacter)) {
					return `Email addresses can't contain “${unsupportedCharacter}”. Please use a different email address.`;
				}
				return isPostmarkCompatibleEmail(value) || errorTpl;
			}
		};
	};

	static RequiredValidation: ValidationRule = {
		isValid: (v: any) => !!v || "This field is required."
	};

	static RequiredValidationFieldObject = (errorTpl: string): ValidationRule => {
		return {
			isValid: (v: any) => !!v || errorTpl
		};
	};

	static RequiredValidationFieldArray = (errorTpl: string): ValidationRule => {
		return {
			isValid: (v: any) => (Array.isArray(v) && v.length > 0) || errorTpl
		};
	};

	static RequiredValidationField = (errorTpl: string): ValidationRule => {
		return {
			isValid: (v: any) => (!!v && v.trim().length > 0) || errorTpl
		};
	};

	static RequiredValidator = (field: string): ValidationRule => {
		return {
			isValid: (v: any) => (!!v && v.trim().length > 0) || `${field} is required.`
		};
	};

	static MaxLengthValidation(param: number, errorTpl: string): ValidationRule {
		return {
			isValid: (v: string) => {
				const str = v?.toString().trim();
				return !str || countCharacters(str) <= param || errorTpl;
			}
		};
	}

	static MaxLengthValidator = (param: number, field: string): ValidationRule => {
		return {
			isValid: (v: string) => {
				const str = v?.toString();
				return !str || countCharacters(str) <= param || `Please enter a shorter ${field}.`;
			}
		};
	};

	/**
	 * Untrimmed counterpart of MaxLengthValidation, with a caller-supplied message (LSP-16848).
	 * MaxLengthValidator is also untrimmed but hard-codes "Please enter a shorter <field>.", so it
	 * cannot carry a field-specific message. Use this for a limit that must match an untrimmed
	 * server-side count, and pair it with `countCharacters` — NOT `countCharactersTrimmed` — for
	 * `:counter-value`, so the displayed count agrees with this verdict (LSP-15742).
	 */
	static MaxLengthValidationUntrimmed(param: number, errorTpl: string): ValidationRule {
		return {
			isValid: (v: string) => {
				const str = v?.toString();
				return !str || countCharacters(str) <= param || errorTpl;
			}
		};
	}

	/**
	 * Code-point counterpart of MaxLengthValidation (LSP-16852/16853/16854/16855/16859). Trims before
	 * measuring, mirroring MaxLengthValidation exactly, but counts with `countCodePoints`
	 * (`@common-ui/utilities/characterCount`) instead of the grapheme-based `countCharacters` — the
	 * app-wide default unit per LSP-17265. `useCharacterLimit.ts`'s `CHARACTER_LIMIT_MODES` pairs this
	 * with its own code-point counter function so the visible counter and this rule can never disagree
	 * about what they are counting. Additive: `MaxLengthValidation` itself is untouched, so its 21
	 * existing grapheme-based consumers are unaffected.
	 */
	static MaxCodePointsValidation(param: number, errorTpl: string): ValidationRule {
		return {
			isValid: (v: string) => {
				const str = stringifyForLengthRule(v);
				// A value that could not be read as text at all fails CLOSED with the overflow message,
				// rather than the `!str` branch below silently treating it as an empty, always-valid
				// string (LSP-16852/16853/16854/16855/16859 break-it round 1).
				if (str === undefined) return errorTpl;
				const trimmed = str.trim();
				return !trimmed || countCodePoints(trimmed) <= param || errorTpl;
			}
		};
	}

	/**
	 * Code-point counterpart of MaxLengthValidationUntrimmed (LSP-16852/16853/16854/16855/16859). Does
	 * NOT trim, mirroring MaxLengthValidationUntrimmed exactly, but counts with `countCodePoints`
	 * instead of `countCharacters` — see MaxCodePointsValidation above for why this exists as a new,
	 * additive factory rather than a change to the existing grapheme-based one.
	 */
	static MaxCodePointsValidationUntrimmed(param: number, errorTpl: string): ValidationRule {
		return {
			isValid: (v: string) => {
				const str = stringifyForLengthRule(v);
				// Fails CLOSED the same way as MaxCodePointsValidation above for a value that could not
				// be read as text at all (LSP-16852/16853/16854/16855/16859 break-it round 1).
				if (str === undefined) return errorTpl;
				return !str || countCodePoints(str) <= param || errorTpl;
			}
		};
	}

	static MinLengthValidation(param: number, errorTpl: string): ValidationRule {
		return {
			isValid: (v: string) => {
				const str = v?.toString().trim();
				return !str || countCharacters(str) >= param || errorTpl;
			}
		};
	}

	static MaxValueValidation(param: number, errorTpl: string): ValidationRule {
		return {
			isValid: (v: unknown) => {
				if (v === null || v === undefined || v === "") return true;
				const num = typeof v === "number" ? v : Number(v);
				if (!Number.isFinite(num)) return true;
				return num <= param || errorTpl;
			}
		};
	}

	static MinValueValidation(param: number, errorTpl: string): ValidationRule {
		return {
			isValid: (v: unknown) => {
				if (v === null || v === undefined || v === "") return true;
				const num = typeof v === "number" ? v : Number(v);
				if (!Number.isFinite(num)) return true;
				return num >= param || errorTpl;
			}
		};
	}

	static CustomRegexValidation(regex: RegExp, message: string): ValidationRule {
		return {
			isValid: (v: string) => {
				return !v || regex.test(v) || message;
			}
		};
	}

	static CustomFunctionValidation(func: (v: any) => boolean | string): ValidationRule {
		return {
			isValid: func
		};
	}

	static PhoneNumberValidation = (errorTpl: string): ValidationRule => {
		return {
			isValid: (v: string) => {
				if (!v) {
					return true;
				}
				// Crash guard (LSP-16692): reject anything past the sane phone-input ceiling
				// BEFORE touching libphonenumber-js, so a pathological paste can never reach
				// the parser. The field turns red and submit is blocked; typing is not silenced.
				if (v.length > FIELD_LENGTHS.PHONE_INPUT) {
					return errorTpl;
				}
				try {
					if (!isValidPhoneNumber(v)) {
						return errorTpl;
					}
					parsePhoneNumberWithError(v, { extract: false });
					return true;
				} catch (error) {
					return errorTpl;
				}
			}
		};
	};

	static NameRule = (errorTpl: string): ValidationRule => {
		return {
			isValid(value: string) {
				if (typeof value !== "string") {
					return errorTpl;
				}
				const regex = /^[a-zA-Z.'\- ]*$/;
				if (!regex.test(value)) {
					return `Please enter your name using alphabets (a-z, A-Z), periods (.), apostrophes ('), hyphens/dashes (-), and spaces.`;
				}
				if (value.trim().indexOf(" ") === -1) {
					return errorTpl;
				}
				return true;
			}
		};
	};

	static isValid = (value: string, rules: ValidationRule[]): boolean | string => {
		for (let i = 0; i < rules.length; i++) {
			const result = rules[i].isValid(value);
			if (isString(result)) return result;
		}
		return true;
	};

	static EmailPhoneValidation = (errorTpl: string): ValidationRule => {
		return {
			isValid(values: string[]) {
				if (values.length > 0) {
					return true;
				}
				return errorTpl;
			}
		};
	};

	static BasicNumberValidation = (errorTpl: string): ValidationRule => {
		return VuetifyValidation.CustomRegexValidation(new RegExp(/^(?=.*\d)\d*\.?\d*$/), errorTpl);
	};

	static DecimalNumberValidation = (errorTpl: string): ValidationRule => {
		return VuetifyValidation.CustomRegexValidation(new RegExp(/^(?=.*\d)\d*\.?\d{0,2}$/), errorTpl);
	};

	/**
	 * Rejects calendar-invalid dates such as 02/30 or 04/31 (LSP-16446). A native
	 * `<input type="date">` does not guard against impossible dates on every
	 * browser, so we parse the value with moment in strict mode against the
	 * accepted formats: a real date round-trips, an impossible one does not.
	 * Empty values pass — the Required validator owns presence.
	 */
	static DateValidation = (errorTpl: string = DEFAULT_DATE_ERROR): ValidationRule => {
		return {
			isValid: (v: unknown) => {
				if (v === null || v === undefined) return true;
				const str = String(v).trim();
				if (str.length === 0) return true;
				const parsed = moment(str, [...ACCEPTED_DATE_FORMATS], true);
				return parsed.isValid() || errorTpl;
			}
		};
	};

	static NumberRequiredValidationField = (errorTpl: string): ValidationRule => {
		return {
			isValid: (v: any) => {
				const str = v == null ? "" : String(v);
				if (str.trim().length === 0) {
					return errorTpl;
				}
				return true;
			}
		};
	};
}

/**
 * Returns true when a value entered into a Number form field is a valid number.
 * Empty values are treated as valid here (presence is enforced separately by a
 * required rule), matching BasicNumberValidation's empty-passes behavior.
 *
 * Single source of truth shared by UserInputFormRenderer's Vuetify rule and the
 * Run-Report gate (useRunAgentUserInput.allUserInputValid) so non-numeric text
 * such as "e" cannot pass the gate. (guards LSP-16496)
 */
export const isNumericFieldValueValid = (value: unknown): boolean => {
	return VuetifyValidation.BasicNumberValidation("").isValid(value == null ? "" : String(value)) === true;
};

/** The subset of a Vuetify `VForm` instance `validateFormOrWarn` below needs. */
export interface ValidatableFormRef {
	validate: () => Promise<{ valid: boolean; errors?: Array<{ id: unknown; errorMessages: string[] }> }>;
}

/** `validateFormOrWarn`'s own well-formed resolution shape, named so both it and its helpers can reference it. */
type FormValidationResult = { valid: boolean; errors?: Array<{ id: unknown; errorMessages: string[] }> };

/**
 * `true` when `result` is a well-formed Vuetify `VForm#validate()` resolution — a real object whose
 * `valid` property is an actual boolean, not merely truthy. Guards `validateFormOrWarn` below against a
 * `formRef.validate()` that resolves something else entirely: `undefined`/`null`, a bare `true`, an
 * object with no `valid` key at all, or — the most dangerous shape — `{ valid: "true" }`, a STRING that
 * is truthy, so a caller doing this helper's own documented `if (!result.valid) return;` pattern would
 * treat it as valid and save anyway (LSP-16852/16853/16854/16855/16859 break-it round 1).
 */
function isWellFormedFormValidationResult(result: unknown): result is FormValidationResult {
	return typeof result === "object" && result !== null && typeof (result as { valid?: unknown }).valid === "boolean";
}

/** Renders a malformed `validate()` resolution for the diagnostic warning below, without ever throwing itself. */
function describeMalformedValidationResult(result: unknown): string {
	if (result === undefined) return "undefined";
	if (result === null) return "null";
	try {
		return JSON.stringify(result) ?? String(result);
	} catch {
		return String(result);
	}
}

/**
 * Shared "check the ref, warn and bail if it's absent, otherwise validate" guard for a save handler
 * driven by a `v-form` template ref. The ref is only populated once the form has mounted, so a stray
 * save call before mount (or after unmount) has nothing else to validate against — this logs a
 * diagnostic naming the component (root CLAUDE.md's "Diagnostic logging when fixing silent failures")
 * and returns `null` so the caller can bail the same way it always did. `warningMessage` is the
 * caller's own message text — this only centralizes the ref-check/warn/validate mechanics, not the
 * wording, since callers' messages differ (e.g. "save" vs "submitForm") and per-site wording isn't the
 * behavior being deduplicated here.
 *
 * Fails CLOSED — resolves `{ valid: false }`, never lets the failure propagate or read as valid — for
 * two additional hostile cases a mounted-but-broken form ref can produce (LSP-16852/16853/16854/16855/
 * 16859 break-it round 1):
 *   - `formRef.validate()` REJECTS (e.g. a field unmounts mid-validation): caught here so every caller's
 *     `await validateFormOrWarn(...)` keeps resolving the documented `{ valid } | null` shape its own
 *     `if (!validationResult) return;` / `if (!validationResult.valid) return;` guards are written
 *     against, instead of throwing out of an `async` save handler no caller wraps in try/catch.
 *   - `formRef.validate()` RESOLVES something other than a real `{ valid: boolean, ... }` object —
 *     `isWellFormedFormValidationResult` above owns exactly what counts as well-formed.
 * Both branches warn with `warningMessage` embedded (naming the same component the ref-absent branch
 * above already does), so the failure is diagnosable the same way.
 */
export async function validateFormOrWarn(
	formRef: ValidatableFormRef | null | undefined,
	warningMessage: string
): Promise<FormValidationResult | null> {
	if (!formRef) {
		console.warn(warningMessage);
		return null;
	}

	let result: unknown;
	try {
		result = await formRef.validate();
	} catch (error) {
		const reason = error instanceof Error ? error.message : String(error);
		console.warn(`${warningMessage} (form.validate() rejected: ${reason}; treating the form as invalid.)`);
		return { valid: false };
	}

	if (!isWellFormedFormValidationResult(result)) {
		console.warn(
			`${warningMessage} (form.validate() returned a malformed result (${describeMalformedValidationResult(result)}); treating the form as invalid.)`
		);
		return { valid: false };
	}

	return result;
}

export { VuetifyValidation };
